import React from "react";

import type { ZohoStepDTO } from "../data/zohoConnectApi";
import { CheckIcon, WarningIcon } from "./icons";
import { cn } from "./cn";

/**
 * Seconds included on purpose. Three of these steps resolve within a second or
 * two of each other, and a minute-only stamp would print the same time on all
 * of them, which reads as a frozen screen rather than a fast one.
 */
function formatTime(iso: string): string {
  const at = new Date(iso);
  if (Number.isNaN(at.getTime())) return "";

  return at.toLocaleTimeString("en-IN", { hour: "2-digit", minute: "2-digit", second: "2-digit" });
}

export function Spinner() {
  return (
    <span className="mt-0.5 size-4 shrink-0 animate-spin rounded-full border-2 border-muted-foreground/30 border-t-muted-foreground" />
  );
}

/**
 * One step of a sequence, exactly as ConnectionSequence draws it. Exported so
 * GroupedSequence can show the same rows inside its groups — a step must look
 * the same whether the list around it is flat or folded.
 */
export function SequenceStepRow({
  step,
  onRetry,
  showDoneDetail,
  showStatusWord,
}: {
  step: ZohoStepDTO;
  onRetry?: () => void;
  showDoneDetail: boolean;
  /**
   * Spell the state out beside the row, for a list where the icon alone is not
   * enough — Setting Up Zoho, where each of the four rows is a setting the CA
   * may have to go and fix in Zoho themselves.
   */
  showStatusWord?: boolean;
}) {
  if (step.status === "failed") {
    return (
      <div className="flex flex-col gap-2 rounded-md border border-amber-500/40 bg-amber-500/5 p-3">
        <div className="flex items-start gap-2 text-sm font-medium">
          <WarningIcon className="mt-0.5 size-4 shrink-0 text-amber-600" />
          <span>
            {step.label}
            {step.at && <span className="ml-1.5 font-normal text-muted-foreground">{formatTime(step.at)}</span>}
          </span>
          {showStatusWord && (
            <span className="ml-auto shrink-0 text-xs font-medium text-amber-700">Failed – Needs Attention</span>
          )}
        </div>
        {/* The reason, not a code. Every message that reaches here is
            written for whoever has to fix it: the client added us as
            Staff, no invitation ever arrived at the address. */}
        {step.error && <p className="pl-6 text-[13px] text-muted-foreground">{step.error}</p>}
        {onRetry && (
          <div className="pl-6">
            <button
              type="button"
              onClick={onRetry}
              className="rounded-md border bg-background px-3 py-[5px] text-[13px] font-medium transition-colors hover:bg-muted/40"
            >
              Try again
            </button>
          </div>
        )}
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-1">
      <div className="flex items-start gap-2 text-sm">
        {step.status === "done" ? (
          <CheckIcon className="mt-0.5 size-4 shrink-0 text-emerald-600" />
        ) : step.status === "running" ? (
          <Spinner />
        ) : (
          // Not started. A hollow marker rather than a greyed tick:
          // the list is the plan as much as the progress, and a step
          // still to come should not look like one that is done.
          <span className="mt-0.5 size-4 shrink-0 rounded-full border-2 border-muted-foreground/20" />
        )}
        <span className={cn(step.status === "pending" && "text-muted-foreground")}>
          {step.label}
          {step.status === "done" && step.at && (
            <span className="ml-1.5 text-muted-foreground">{formatTime(step.at)}</span>
          )}
        </span>
        {showStatusWord && step.status !== "pending" && (
          <span
            className={cn(
              "ml-auto shrink-0 text-xs font-medium",
              step.status === "done" ? "text-emerald-700" : "text-muted-foreground"
            )}
          >
            {step.status === "done" ? "Completed" : "In Progress"}
          </span>
        )}
      </div>
      {(step.status === "running" || (showDoneDetail && step.status === "done")) && step.detail && (
        <p className="pl-6 text-[13px] text-muted-foreground">{step.detail}</p>
      )}
    </div>
  );
}

/**
 * The connection coming up, one row per step, with the time each one landed.
 *
 * Every row's wording, status and timestamp comes from the server. Nothing here
 * decides how far along anything is — two separate machines drive this (the
 * connect agent and the read-back) and working out which row they add up to is
 * exactly the sort of thing that goes wrong quietly when two clients do it
 * differently.
 *
 * Never a bare spinner: a running step always says what is happening or what to
 * check while it happens, because these steps can genuinely take minutes and a
 * spinner alone gives a CA nothing to do and no idea whether to keep waiting.
 */
export function ConnectionSequence({
  title,
  steps,
  onRetry,
  showDoneDetail = false,
  showStatusWord = false,
}: {
  title: string;
  steps: ZohoStepDTO[];
  onRetry?: () => void;
  /**
   * Also print a finished step's detail under its tick. Off by default: on the
   * connection screen a done step's detail is what it said WHILE running, which
   * is stale the moment it lands. The prepare screen opts in because there the
   * detail is the outcome — what was found or changed — written after the fact.
   */
  showDoneDetail?: boolean;
  /** Spell each row's state out in words. See SequenceStepRow. */
  showStatusWord?: boolean;
}) {
  return (
    <div className="flex flex-col gap-4 rounded-lg border p-6">
      <h3 className="text-[17px] font-medium">{title}</h3>

      <ul className="flex flex-col gap-3">
        {steps.map((step) => (
          <li key={step.key} className="flex flex-col gap-2 border-t pt-3 first:border-t-0 first:pt-0">
            <SequenceStepRow
              step={step}
              onRetry={onRetry}
              showDoneDetail={showDoneDetail}
              showStatusWord={showStatusWord}
            />
          </li>
        ))}
      </ul>
    </div>
  );
}
